/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea,
  CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const UnitInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    unitName: '',
    unitDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        unitName: initialData.UnitName || '',
        unitDescription: initialData.UnitDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) setFormData({ unitName: '', unitDescription: '' });
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.unitName.trim()) newErrors.unitName = t('unitNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: 'User session not found. Please log in again.' });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      UnitName: formData.unitName.trim(),
      UnitDescription: formData.unitDescription.trim(),
    };

    if (isEdit && initialData?.UnitId) {
      payload.UnitId = initialData.UnitId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ unitName: '', unitDescription: '' });
    setErrors({});
    onCancel();
  };

  return (
    <div>
      {showSuccess && <CAlert color="success">{t('unitCreated')}</CAlert>}
      {errors.general && <CAlert color="danger">{errors.general}</CAlert>}
      {submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode !== 0 && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription || t('somethingWentWrong')}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="unitName">{t('unitName')}</CFormLabel>
            <CFormInput
              type="text"
              id="unitName"
              name="unitName"
              value={formData.unitName}
              onChange={handleInputChange}
              invalid={!!errors.unitName}
              disabled={isLoading}
              placeholder={t('enterUnitName')}
            />
            {errors.unitName && <div className="invalid-feedback d-block">{errors.unitName}</div>}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="unitDescription">{t('description')}</CFormLabel>
            <CFormTextarea
              id="unitDescription"
              name="unitDescription"
              rows={4}
              value={formData.unitDescription}
              onChange={handleInputChange}
              invalid={!!errors.unitDescription}
              disabled={isLoading}
              placeholder={t('enterUnitDescription')}
            />
            {errors.unitDescription && (
              <div className="invalid-feedback d-block">{errors.unitDescription}</div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading
                ? (<><CSpinner size="sm" className="me-2" /> {t('saving')}</>)
                : (<><CIcon icon={cilSave} className="me-2" /> {t('save')}</>)}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" /> {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

UnitInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default UnitInputs;
